import { PropsWithChildren, useCallback } from 'react';

import { assignInlineVars } from '@vanilla-extract/dynamic';
import cn from 'classnames';

import { colors } from '../../../colors';
import { u } from '../../../styles';
import { typography } from '../../../typography';
import {
    ChartLineIcon,
    ChatTextIcon,
    ComparisonIcon,
    CorridorsIcon,
    EditIcon,
    FilterIcon,
    GoalIcon,
    type IconWeight,
    MonitorIcon,
    PersonIcon,
    ScribbleIcon,
    ThreeDIcon,
    ToggleLeftIcon,
    ToggleRightIcon,
} from '../../icons';

import * as css from './ToggleButton.css';
import { useControllableToggle } from './useControllableToggle';

import type { ToggleButtonVariants } from './ToggleButton.css';

const availableIcons = {
    'chart-line': { component: ChartLineIcon, size: 14 },
    filter: { component: FilterIcon, size: 14 },
    corridor: { component: CorridorsIcon, size: 14 },
    comparison: { component: ComparisonIcon, size: 14 },
    person: { component: PersonIcon, size: 14 },
    threed: { component: ThreeDIcon, size: 14 },
    notes: { component: ChatTextIcon, size: 14 },
    goals: { component: GoalIcon, size: 14 },
    edit: { component: EditIcon, size: 14 },
    monitor: { component: MonitorIcon, size: 14 },
    scribble: { component: ScribbleIcon, size: 14 },
};

export interface BaseToggleButtonProps {
    /**
     * Current active state (controlled mode).
     * When provided, the component does not manage its own internal state.
     */
    isActive?: boolean;

    /**
     * Disables interaction and applies disabled styling.
     */
    isDisabled?: boolean;

    /**
     * Applies a "stroke" style treatment (outline-like variant).
     */
    stroke?: boolean;

    /**
     * When true, the button stretches to fill the available width.
     */
    fillWidth?: boolean;

    /**
     * Called when the toggle state changes.
     * Fires in both controlled and uncontrolled modes.
     */
    onChange?: (isActive: boolean) => void;

    /**
     * Visual variant for the toggle button (defined by `ToggleButton.css`).
     */
    variant?: ToggleButtonVariants['variant'];
}

export function BaseToggleButton({
    isDisabled,
    isActive,
    variant,
    fillWidth,
    stroke,
    children,
    onChange,
}: PropsWithChildren<BaseToggleButtonProps>) {
    const { value, toggle } = useControllableToggle({
        controlledValue: isActive,
        onChange,
    });

    return (
        <button
            disabled={isDisabled}
            className={css.toggleButton({
                status: isDisabled ? 'disabled' : value ? 'active' : 'inactive',
                variant,
                fillWidth,
                stroke,
            })}
            onClick={toggle}
        >
            {children}
        </button>
    );
}

export function ToggleButtonV2({
    isDisabled,
    isActive,
    children,
    onChange,
    onClick,
}: PropsWithChildren<BaseToggleButtonProps & { onClick?: () => void }>) {
    const { value, toggle } = useControllableToggle({
        controlledValue: isActive,
        onChange,
    });

    const valueWithDisable = isDisabled ? false : value;

    return (
        <div className={css.toggleButtonWrapperV2} onClick={toggle}>
            <button
                disabled={isDisabled}
                className={css.toggleButtonV2({
                    enabled: valueWithDisable,
                })}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!onClick || !value) toggle();
                    onClick?.();
                }}
            >
                {children}
            </button>
            {valueWithDisable ? (
                <ToggleRightIcon weight="fill" size={u(3)} color={colors.blue[500]} />
            ) : (
                <ToggleLeftIcon size={u(3)} />
            )}
        </div>
    );
}

export interface ToggleButtonProps extends Omit<BaseToggleButtonProps, 'stroke'> {
    /**
     * Button label text.
     */
    children: string;

    /**
     * Optional icon key displayed before the label.
     */
    icon?: keyof typeof availableIcons;

    /**
     * Icon weight passed to the icon component.
     *
     * @default 'fill'
     */
    iconWeight?: IconWeight;

    /**
     * When provided, enables ellipsis behavior after a configured number of letters.
     * Also adds a `title` attribute so the full label is available on hover.
     */
    maxLetters?: number;

    /**
     * Typography variant applied to the label.
     *
     * @default 'h4'
     */
    typographyVariant?: 'h3' | 'h4';
}

export function ToggleButton({
    children,
    variant = 'lighter',
    icon,
    iconWeight = 'fill',
    maxLetters,
    typographyVariant = 'h4',
    fillWidth = false,
    ...baseProps
}: ToggleButtonProps) {
    const showEllipsis = maxLetters !== undefined;
    const iconConfig = icon ? availableIcons[icon] : null;
    const Icon = iconConfig?.component;

    return (
        <BaseToggleButton {...baseProps} variant={variant} fillWidth={fillWidth}>
            {Icon && (
                <span className={css.icon}>
                    <Icon weight={iconWeight} width={iconConfig.size} height={iconConfig.size} />
                </span>
            )}
            <span
                title={showEllipsis ? children : undefined}
                style={assignInlineVars({
                    [css.maxLetters]: `${maxLetters ?? 0}`,
                })}
                className={cn(
                    typography({ variant: typographyVariant }),
                    css.content({
                        ellipsis: showEllipsis,
                        radio: variant === 'radio',
                    }),
                )}
            >
                {children}
            </span>
        </BaseToggleButton>
    );
}

export interface ToggleButtonWithValueProps<T> extends Omit<ToggleButtonProps, 'onChange'> {
    /**
     * Value associated with this button.
     */
    value: T;

    /**
     * Called when the button is selected.
     */
    onSelect: (value: T) => void;
}

export function ToggleButtonWithValue<T>({ value, onSelect, ...rest }: ToggleButtonWithValueProps<T>) {
    const onChange = useCallback(() => {
        onSelect(value);
    }, [onSelect, value]);

    return <ToggleButton {...rest} onChange={onChange} />;
}
